'use client';

import { c as _c } from "react/compiler-runtime";
import React, { useState, useEffect } from 'react';
import { cn } from '@/lib/utils';
import { Box } from '@/components/atoms/Box';
import { Img } from '@/components/atoms/Img';
import { Video } from '@/components/atoms/Video';
import { getVidyardMp4Url } from '@/lib/vidyard';
interface BackgroundVideoVidyardProps {
  vidyardId: string;
  posterUrl: string;
  posterSrcSet?: string;
  posterSizes?: string;
  priority?: boolean;
  className?: string;
  width?: number;
  height?: number;
}

/**
 * Vidyard-sourced background video, rendered as a native chromeless <video>.
 *
 * AQSN-172: Vidyard's `renderPlayer({ type: 'background' })` is not a real
 * embed type — Vidyard only supports 'inline' and 'lightbox' — so the SDK
 * silently falls back to 'inline', where the muted-autoplay "Click to
 * unmute" overlay is a built-in Vidyard UX pattern that cannot be suppressed
 * via config, because it renders inside a cross-origin iframe our CSS/JS can
 * never reach. Instead, this component resolves a direct MP4 URL from
 * Vidyard's public metadata endpoint (src/lib/vidyard.ts) and renders it as
 * a native <video> element, which has no possible chrome by construction.
 *
 * Renders a poster image immediately for LCP, resolves the Vidyard MP4 URL
 * on mount (cheap metadata fetch, doesn't block LCP), then defers mounting
 * the actual <video> element until the browser is idle after page load —
 * matching the sibling BackgroundVideo component's timing model. Crossfades
 * from poster to video once playable.
 */
export const BackgroundVideoVidyard = t0 => {
  const $ = _c(30);
  const {
    vidyardId,
    posterUrl,
    posterSrcSet,
    posterSizes,
    priority: t1,
    className,
    width,
    height
  } = t0;
  const priority = t1 === undefined ? false : t1;
  const [mp4Url, setMp4Url] = useState(null);
  const [isVideoReady, setIsVideoReady] = useState(false);
  const [shouldLoadVideo, setShouldLoadVideo] = useState(false);
  let t2;
  let t3;
  if ($[0] !== vidyardId) {
    t2 = () => {
      let cancelled = false;
      getVidyardMp4Url(vidyardId).then(url => {
        if (!cancelled) {
          setMp4Url(url);
        }
      });
      return () => {
        cancelled = true;
      };
    };
    t3 = [vidyardId];
    $[0] = vidyardId;
    $[1] = t2;
    $[2] = t3;
  } else {
    t2 = $[1];
    t3 = $[2];
  }
  useEffect(t2, t3);
  let t4;
  let t5;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = () => {
      const loadVideo = () => {
        if ("requestIdleCallback" in window) {
          (window as Window & {
            requestIdleCallback: (cb: () => void) => void;
          }).requestIdleCallback(() => setShouldLoadVideo(true));
        } else {
          setTimeout(() => setShouldLoadVideo(true), 2000);
        }
      };
      if (document.readyState === "complete") {
        loadVideo();
      } else {
        window.addEventListener("load", loadVideo, {
          once: true
        });
        return () => window.removeEventListener("load", loadVideo);
      }
    };
    t5 = [];
    $[3] = t4;
    $[4] = t5;
  } else {
    t4 = $[3];
    t5 = $[4];
  }
  useEffect(t4, t5);
  let t6;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = () => setIsVideoReady(true);
    $[5] = t6;
  } else {
    t6 = $[5];
  }
  const handleCanPlayThrough = t6;
  let t7;
  if ($[6] !== className) {
    t7 = cn("absolute inset-0 overflow-hidden", className);
    $[6] = className;
    $[7] = t7;
  } else {
    t7 = $[7];
  }
  const t8 = priority ? "eager" : "lazy";
  const t9 = priority ? "sync" : "async";
  const t10 = priority ? "high" : undefined;
  const t11 = isVideoReady ? "opacity-0" : "opacity-100";
  let t12;
  if ($[8] !== t11) {
    t12 = cn("absolute inset-0 size-full object-cover transition-opacity duration-700", t11);
    $[8] = t11;
    $[9] = t12;
  } else {
    t12 = $[9];
  }
  let t13;
  if ($[10] !== height || $[11] !== posterSizes || $[12] !== posterSrcSet || $[13] !== posterUrl || $[14] !== t10 || $[15] !== t12 || $[16] !== t8 || $[17] !== t9 || $[18] !== width) {
    t13 = <Img src={posterUrl} srcSet={posterSrcSet} sizes={posterSizes} alt="Background video poster" aria-hidden="true" loading={t8} decoding={t9} fetchPriority={t10} width={width} height={height} className={t12} />;
    $[10] = height;
    $[11] = posterSizes;
    $[12] = posterSrcSet;
    $[13] = posterUrl;
    $[14] = t10;
    $[15] = t12;
    $[16] = t8;
    $[17] = t9;
    $[18] = width;
    $[19] = t13;
  } else {
    t13 = $[19];
  }
  let t14;
  if ($[20] !== height || $[21] !== isVideoReady || $[22] !== mp4Url || $[23] !== shouldLoadVideo || $[24] !== width) {
    t14 = shouldLoadVideo && mp4Url && <Video autoPlay={true} muted={true} loop={true} playsInline={true} preload="auto" onCanPlayThrough={handleCanPlayThrough} className={cn("absolute inset-0 size-full object-cover transition-opacity duration-700", isVideoReady ? "opacity-100" : "opacity-0")} width={width} height={height}><source src={mp4Url} type="video/mp4" /></Video>;
    $[20] = height;
    $[21] = isVideoReady;
    $[22] = mp4Url;
    $[23] = shouldLoadVideo;
    $[24] = width;
    $[25] = t14;
  } else {
    t14 = $[25];
  }
  let t15;
  if ($[26] !== t13 || $[27] !== t14 || $[28] !== t7) {
    t15 = <Box className={t7}>{t13}{t14}</Box>;
    $[26] = t13;
    $[27] = t14;
    $[28] = t7;
    $[29] = t15;
  } else {
    t15 = $[29];
  }
  return t15;
};